import React, { useState, useEffect,useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";
import toast from "react-hot-toast";
import "./ExamensMedicaux.css";
import { saveDossier } from "../../redux/dispatch";
import SaveLoader from "../shared/SaveLoader";
import { ThreeDots } from "react-loader-spinner";

const RUBRIQUE = "examens_medicaux";

const MenuBar = ({ editor }) => {
  if (!editor) return null;
  const buttons = [
    { label: "G",       title: "Gras",           action: () => editor.chain().focus().toggleBold().run(),                  active: editor.isActive("bold"),           style: { fontWeight: "bold" } },
    { label: "I",       title: "Italique",        action: () => editor.chain().focus().toggleItalic().run(),                active: editor.isActive("italic"),         style: { fontStyle: "italic" } },
    { label: "S",       title: "Barré",           action: () => editor.chain().focus().toggleStrike().run(),                active: editor.isActive("strike"),         style: { textDecoration: "line-through" } },
    { label: "U",       title: "Souligné",        action: () => editor.chain().focus().toggleUnderline().run(),             active: editor.isActive("underline"),      style: { textDecoration: "underline" } },
    { label: "H1",      title: "Titre 1",         action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),   active: editor.isActive("heading", { level: 1 }) },
    { label: "H2",      title: "Titre 2",         action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),   active: editor.isActive("heading", { level: 2 }) },
    { label: "• List",  title: "Liste à puces",   action: () => editor.chain().focus().toggleBulletList().run(),            active: editor.isActive("bulletList") },
    { label: "1. List", title: "Liste numérotée", action: () => editor.chain().focus().toggleOrderedList().run(),           active: editor.isActive("orderedList") },
    { label: "❝",       title: "Citation",        action: () => editor.chain().focus().toggleBlockquote().run(),            active: editor.isActive("blockquote") },
    { label: "—",       title: "Séparateur",      action: () => editor.chain().focus().setHorizontalRule().run(),           active: false },
    { label: "↩",       title: "Annuler",         action: () => editor.chain().focus().undo().run(),                       active: false },
    { label: "↪",       title: "Rétablir",        action: () => editor.chain().focus().redo().run(),                       active: false },
  ];
  return (
    <div className="em-menubar">
      {buttons.map((btn, i) => (
        <button key={i} title={btn.title}
          className={`em-menubar-btn ${btn.active ? "em-menubar-btn--active" : ""}`}
          style={btn.style || {}} onClick={btn.action} type="button">
          {btn.label}
        </button>
      ))}
    </div>
  );
};
const EXTENSIONS = [StarterKit, Underline];
const PageEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: content || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || "");
    }
  }, [content]);

  return (
    <div className="em-editor-wrapper">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="em-editor" />
    </div>
  );
};

const EMPTY_EXAMEN = () => ({ data: "" });

const ExamensMedicaux = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { id }    = useParams();
const [saving, setSaving] = useState(false);
  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;

  const formulairesData  = useSelector((s) => s.formulaires.data);
  const savedExamens = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.examens || [EMPTY_EXAMEN()]);
  const rawImages    = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const [examens,    setExamens]    = useState(savedExamens);
  const [activeExamen, setActiveExamen] = useState(() => {
    try {
      const s = parseInt(sessionStorage.getItem(`em_activeExamen_${id}`), 10);
      return Number.isInteger(s) && s >= 0 ? s : 0;
    } catch (e) { return 0; }
  });
  const [imgIndex,   setImgIndex]   = useState(0);
  const [imgLoading, setImgLoading] = useState(true);
  const [gotoValue,  setGotoValue]  = useState("");
  const imgRef = useRef(null);
  const examensRef = useRef(examens);
  useEffect(() => { examensRef.current = examens; }, [examens]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setExamens(savedExamens);
    const s = parseInt(sessionStorage.getItem(`em_activeExamen_${id}`), 10);
    setActiveExamen(Number.isInteger(s) && s >= 0 ? s : 0);
  }, [id]);

  useEffect(() => {
    if (activeExamen >= examens.length) setActiveExamen(examens.length - 1);
  }, [examens.length, activeExamen]);

  // Persiste l'index d'examen courant (survit au changement de section / retour) — frontend only
  useEffect(() => {
    try { sessionStorage.setItem(`em_activeExamen_${id}`, String(activeExamen)); } catch (e) {}
  }, [activeExamen, id]);

  // Aligne l'image sur l'examen courant (y compris au retour dans la section)
  useEffect(() => {
    setImgIndex(activeExamen < IMAGES.length ? activeExamen : IMAGES.length - 1);
  }, [activeExamen, IMAGES.length]);

  // Réinitialise le spinner d'image à chaque changement d'image (gère le cas image déjà en cache)
  useEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) setImgLoading(false);
    else setImgLoading(true);
  }, [imgIndex]);


// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
  const handleChangeExamen = (index, html) => {
    setExamens((prev) => {
      const updated = [...prev];
      updated[index] = { data: html };
      return updated;
    });
  };

  const handleAddExamen = () => {
    const newIndex = examens.length;
    setExamens((prev) => [...prev, EMPTY_EXAMEN()]);
    setActiveExamen(newIndex);
    setImgIndex(newIndex < IMAGES.length ? newIndex : IMAGES.length - 1);
  };

  const handleDeleteExamen = (index) => {
    setExamens((prev) => prev.filter((_, i) => i !== index));
    setActiveExamen((prev) => Math.max(0, prev - 1));
  };

const handleSaveAll = async () => {
  setSaving(true);
  dispatch(updateFormData(id, RUBRIQUE, "examens", examens));
  try {
      const result = await dispatch(saveDossier());
      toast.success(result.message);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    }finally {
    setSaving(false);
  }
};

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "examens", examensRef.current));
  };

    useEffect(() => {
      return () => {
        handleSave();
      };
    }, []);

  

  const getPaginationItems = (total, current) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const items = [];
  items.push(0);
  if (current > 3) items.push("...");
  for (let i = Math.max(1, current - 1); i <= Math.min(total - 2, current + 1); i++) {
    items.push(i);
  }
  if (current < total - 4) items.push("...");
  items.push(total - 1);
  return items;
};

const goToExamen = (index) => {
  if (index < 0 || index >= examens.length) return;
  setActiveExamen(index);
  setImgIndex(index < IMAGES.length ? index : IMAGES.length - 1);
};

const handleGoto = () => {
  const n = parseInt(gotoValue, 10);
  if (Number.isInteger(n) && n >= 1 && n <= examens.length) {
    goToExamen(n - 1);
    setGotoValue("");
  }
};

  const handleCancel = () => setExamens(savedExamens);

  if (!dossier) {
    return (
      <div className="em-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="em-page">
        {saving && <SaveLoader />}
      <header className="em-header">
        <div className="em-header-left">
          <button className="em-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="em-title">Dossier {dossier.numero_dossier || dossier.id}</h1>
            <p className="em-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="em-save-btn" onClick={handleSaveAll} disabled={saving}>Sauvegarder les modifications</button>
      </header>

      <div className="em-body">
        <aside className="em-image-panel">
          <div className="em-image-header">
            <span className="em-rubrique-label">Examens Médicaux</span>
            <span className="em-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="em-image-viewer" style={{ position: "relative" }}>
            {imgLoading && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.6)", zIndex: 2 }}>
                <ThreeDots visible={true} height="60" width="60" color="#4fa94d" radius="9" ariaLabel="chargement-image" />
              </div>
            )}
            <img
              ref={imgRef}
              src={IMAGES[imgIndex]}
              alt={`Page ${imgIndex + 1}`}
              className="em-image"
              onLoad={() => setImgLoading(false)}
              onError={() => setImgLoading(false)}
            />
          </div>
          <div className="em-image-nav">
          <button className="em-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
          {getPaginationItems(IMAGES.length, imgIndex).map((item, i) =>
            item === "..." ? (
              <span key={i} className="em-ellipsis">…</span>
            ) : (
              <button key={i}
                className={`em-nav-dot-btn ${imgIndex === item ? "em-nav-dot-btn--active" : ""}`}
                onClick={() => setImgIndex(item)}>
                {item + 1}
              </button>
            )
          )}
          <button className="em-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
        </div>
        </aside>

        <main className="em-form-panel">
          <div className="em-form-scroll">

            {/* ── Pagination examens ── */}
          <div className="em-pages-nav">
              <div className="em-pages-tabs">
                <button className="em-nav-page-btn" onClick={() => goToExamen(activeExamen - 1)} disabled={activeExamen === 0}>‹</button>
                {getPaginationItems(examens.length, activeExamen).map((item, i) =>
                  item === "..." ? (
                    <span key={i} className="em-ellipsis">…</span>
                  ) : (
                    <button key={i}
                      className={`em-page-tab ${activeExamen === item ? "em-page-tab--active" : ""}`}
                      onClick={() => goToExamen(item)}>
                      {item + 1}
                    </button>
                  )
                )}
                <button className="em-nav-page-btn" onClick={() => goToExamen(activeExamen + 1)} disabled={activeExamen === examens.length - 1}>›</button>
              </div>
              <div className="em-goto" style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginLeft: "8px" }}>
                <input type="number" min={1} max={examens.length} placeholder="N°" value={gotoValue}
                  onChange={(e) => setGotoValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleGoto(); }}
                  style={{ width: "68px", padding: "5px 7px", border: "1px solid #cbd5e1", borderRadius: "6px", fontSize: "13px" }} />
                <button type="button" onClick={handleGoto}
                  style={{ padding: "5px 12px", border: "none", borderRadius: "6px", background: "#2563eb", color: "#fff", fontSize: "13px", cursor: "pointer", fontWeight: 600 }}>Aller</button>
                <span style={{ fontSize: "12px", color: "#64748b" }}>/ {examens.length}</span>
              </div>
              <button className="em-add-page-btn" onClick={handleAddExamen}>+ Ajouter un examen</button>
          </div>

            {/* ── Éditeur ── */}
            <PageEditor
              key={activeExamen}
              content={examens[activeExamen]?.data || ""}
              onChange={(html) => handleChangeExamen(activeExamen, html)}
            />

            {/* ── Supprimer ── */}
            {activeExamen > 0 && (
              <button className="em-delete-page-btn" onClick={() => handleDeleteExamen(activeExamen)}>
                🗑 Supprimer cet examen
              </button>
            )}

          </div>

         {/*  <div className="em-form-actions">
            <button className="em-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="em-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}


        <RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />        </main>
      </div>
    </div>
  );
};

export default ExamensMedicaux;